import api from './api';

/**
 * EDI Charge Code Mapping (cms.edi_chargecodemap) — one row per (EDI handle,
 * invoice line-item type) -> charge code on the outbound 210, presented GROUPED by
 * handle. The grid goes through the /tablemaint engine (key 'edi-chargecodemap',
 * read-only); this service backs the bespoke drawer: the handle dropdown + line-item
 * types, the DEFAULT base map shown as reference, a handle's own overrides, and the
 * set-reconcile save.
 */
export interface Option { value: string; label: string; }

export interface EdiChargeCodeOptions {
  /** DEFAULT first, then the 210-enabled partners, then the hand-listed stragglers. */
  edihandles: Option[];
  /** Active cms.invoice_litypes — `value` is the recno as a string. */
  litypes: Option[];
  /** Handles that already have a mapping — hidden from the Add dropdown. */
  mapped_handles: string[];
}

export interface ChargeCodeLine {
  /** 0 for a row added in the drawer and not yet saved. */
  recno: number;
  litype_recno: number;
  litype_name: string;
  /** The line-item type's OWN soft-delete flag — 'Y' when it has been retired. */
  litype_deleted?: string;
  chargecode: string;
  /** What DEFAULT maps this line item to; undefined when DEFAULT has no row for it. */
  default_code?: string | null;
}

/** A soft-deleted mapping, restorable from the drawer. */
export interface RemovedChargeCodeLine extends ChargeCodeLine {
  removed_by?: string;
  removed_at?: string;
}

export interface DefaultMapLine {
  litype_recno: number;
  litype_name: string;
  chargecode: string;
}

export async function getEdiChargeCodeOptions(): Promise<EdiChargeCodeOptions> {
  const res = await api.get('/edi-chargecodes/form-options');
  return res.data as EdiChargeCodeOptions;
}

export async function getEdiChargeCodeDefaultMap(): Promise<DefaultMapLine[]> {
  const res = await api.get('/edi-chargecodes/default-map');
  return (res.data?.lines ?? []) as DefaultMapLine[];
}

/** A handle's live mappings and its removed ones, kept apart so a removed row
 *  cannot ride along into a save. Restore = move it into `lines` and save; the
 *  endpoint reactivates that recno rather than inserting a duplicate. */
export async function getEdiChargeCodeLines(
  edihandle: string,
): Promise<{ lines: ChargeCodeLine[]; removed: RemovedChargeCodeLine[] }> {
  const res = await api.get('/edi-chargecodes/lines', { params: { edihandle } });
  return {
    lines: (res.data?.lines ?? []) as ChargeCodeLine[],
    removed: (res.data?.removed ?? []) as RemovedChargeCodeLine[],
  };
}

/** Reconcile a handle's whole set: `lines` is the desired state, `deletes` the recnos removed. */
export async function saveEdiChargeCodeLines(
  edihandle: string,
  lines: { recno: number; litype_recno: number; chargecode: string }[],
  deletes: number[],
): Promise<void> {
  await api.put('/edi-chargecodes/lines', { edihandle, lines, deletes });
}

/** Edit-history endpoint for a whole EDI handle (passed to EditHistoryLink). */
export function ediChargeCodesHistoryEndpoint(edihandle: string): string {
  return `/edi-chargecodes/history?edihandle=${encodeURIComponent(edihandle)}`;
}
